import { useCallback, useEffect, useState } from 'react';

/**
 * `attr` (default `data-logjob-section`) names the attribute the section
 * elements carry, so the full-screen estimate builder reuses this hook with its
 * own (`data-estimate-section`) — one observer implementation, two callers.
 *
 * Phone-only section navigation for the full-page Log Job (Direction 1).
 * Tracks which `[data-logjob-section]` is on screen inside `scrollEl` and
 * exposes `jumpTo(key)`. Timer/event free: an IntersectionObserver rooted on
 * the scroll container, so a dropped animation event can never strand it
 * (CLAUDE.md's animationend rule). Degrades to "no active pill" where the
 * observer is unavailable (jsdom); jumping still works there.
 *
 * `scrollEl` MUST be real DOM node state (a callback ref), not a `useRef`
 * whose `.current` is read once — the skeleton render mounts no scroll node
 * (`null`), and the `layout`/epoch remount swaps in a brand-new node neither
 * of which changes a `useRef` identity the effect could depend on. State
 * re-renders on both transitions, so the effect (deps `[scrollEl, keys,
 * enabled, attr]`) re-creates the observer exactly when the real node appears.
 */
export function useSectionJump({ scrollEl, keys, enabled, reduced, attr = 'data-logjob-section' }) {
  const [activeKey, setActiveKey] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const root = scrollEl;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    const els = keys
      .map((k) => root.querySelector(`[${attr}="${k}"]`))
      .filter(Boolean);
    if (els.length === 0) return undefined;
    const io = new IntersectionObserver((entries) => {
      // The topmost intersecting section wins; ties resolve to the first in
      // document order, which is also the first in `keys`.
      const hit = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => keys.indexOf(a.target.getAttribute(attr)) - keys.indexOf(b.target.getAttribute(attr)))[0];
      if (hit) setActiveKey(hit.target.getAttribute(attr));
    }, { root, rootMargin: '-40% 0px -55% 0px', threshold: 0 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [scrollEl, keys, enabled, attr]);

  const jumpTo = useCallback((key) => {
    const root = scrollEl;
    const el = root?.querySelector?.(`[${attr}="${key}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }, [scrollEl, reduced, attr]);

  return { activeKey: enabled ? activeKey : null, jumpTo };
}

export default useSectionJump;
